// App.js
import React, { useState, useRef, useEffect, Suspense, useMemo, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { UniverseBackground } from './components/UniverseBackground';
import { Stars, Html, OrbitControls } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette, SSAO, ChromaticAberration, Noise, DepthOfField } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import * as THREE from 'three';
import { Planet } from './components/Planet';
import { Sun } from './components/Sun';
import { ExperienceDetails } from './components/ExperienceDetails';
import { ProjectDetails } from './components/ProjectDetails';
import { Timeline } from './components/Timeline';
import { resumeData } from './defaultData'; // 使用默认数据结构代替 data.js
import { loadAllContent } from './utils/markdownLoader';
import { EducationDetails } from './components/EducationDetails';

// 后期处理效果组件已在顶部导入
import './App.css';

// 相机动画控制组件
const CameraAnimation = ({ selectedExperience, selectedProject }) => {
  const { camera } = useThree();
  const initialCameraRef = useRef(null);
  const startTimeRef = useRef(null);
  const isInitialAnimationRef = useRef(true);
  const deviceTypeRef = useRef(isMobileDevice() ? 'mobile' : 'desktop');
  
  // 保存初始相机位置
  useEffect(() => {
    if (!initialCameraRef.current) {
      initialCameraRef.current = camera.position.clone();
      startTimeRef.current = Date.now();
    }
    
    // 监听窗口大小变化，更新设备类型
    const handleResize = () => {
      const currentDeviceType = isMobileDevice() ? 'mobile' : 'desktop';
      if (deviceTypeRef.current !== currentDeviceType) {
        deviceTypeRef.current = currentDeviceType;
        // 如果设备类型发生变化，重置动画状态
        if (!selectedExperience && !selectedProject) {
          isInitialAnimationRef.current = true;
          startTimeRef.current = Date.now();
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [camera, selectedExperience, selectedProject]);
  
  useFrame(() => {
    if (!initialCameraRef.current) return;
    
    // 初始动画：视角由太阳内部（近）拉远到正常位置（持续3秒）
    if (isInitialAnimationRef.current && Date.now() - startTimeRef.current < 2000) {
      const progress = Math.min((Date.now() - startTimeRef.current) / 2000, 1);
      const easedProgress = easeOutCubic(progress); // 使用缓动函数
      
      // 从太阳内部（非常近）开始
      const startPos = initialCameraRef.current.clone().multiplyScalar(0.1); // 非常近，只有0.1倍距离
      const targetPos = initialCameraRef.current.clone();
      
      // 插值计算当前位置
      camera.position.x = startPos.x + (targetPos.x - startPos.x) * easedProgress;
      camera.position.y = startPos.y + (targetPos.y - startPos.y) * easedProgress;
      camera.position.z = startPos.z + (targetPos.z - startPos.z) * easedProgress;
      
      // 如果动画完成
      if (progress >= 1) {
        isInitialAnimationRef.current = false;
      }
    } 
    // 持续的缓慢移动效果（与行星旋转相反方向）
    else if (!selectedExperience && !selectedProject) { // 只在没有选中行星或项目时进行缓慢移动
      // 缓慢移动视角，与行星旋转相反
      const rotationSpeed = 0.05; // 控制移动速度
      const radius = Math.sqrt(camera.position.x * camera.position.x + camera.position.z * camera.position.z);
      const currentAngle = Math.atan2(camera.position.z, camera.position.x);
      const newAngle = currentAngle - rotationSpeed * 0.002; // 非常缓慢的旋转
      
      camera.position.x = radius * Math.cos(newAngle);
      camera.position.z = radius * Math.sin(newAngle);
    }
  });
  
  return null;
};

// 缓动函数
function easeOutCubic(x) {
  return 1 - Math.pow(1 - x, 3);
}

// 光环轨道组件 - 使用环形几何体

const OrbitLineComponent = React.memo(({ radius, color }) => {
  const fixedRingWidth = 0.8; // 固定光环宽度，不再与半径成比例
  
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <ringGeometry args={[radius - fixedRingWidth/2, radius + fixedRingWidth/2, 64]} />
      <meshBasicMaterial 
        color={color} 
        transparent={true} 
        opacity={0.15}
        side={THREE.DoubleSide}
        blending={THREE.AdditiveBlending} // 增加光照混合
        depthWrite={false} // 避免深度写入问题
      />
    </mesh>
  );
});

// 单个闪烁星星组件
const TwinklingStar = ({ position }) => {
  const starRef = useRef();
  const [opacity, setOpacity] = useState(Math.random());
  const [scale] = useState(1); // setScale 暂时未使用，但保留以备将来使用

  useFrame(({ clock }) => {
    // Create a slower, more random twinkle
    const time = clock.getElapsedTime();
    const randomFactor = Math.sin(time * (0.1 + Math.random() * 0.2) + position[0] * 10); // Add position influence for variety
    const newOpacity = THREE.MathUtils.lerp(0.2, 1.0, (randomFactor + 1) / 2); // Modulate between 0.2 and 1.0
    setOpacity(newOpacity);

    if (starRef.current) {
      starRef.current.material.opacity = opacity;
    }
  });

  return (
    <mesh ref={starRef} position={position} scale={[scale, scale, scale]}>
      <sphereGeometry args={[0.05, 8, 8]} /> {/* Smaller, simpler geometry */}
      <meshStandardMaterial emissive="white" emissiveIntensity={2} transparent depthWrite={false} />
    </mesh>
  );
};

// 注意: 原来的ProjectAsteroid组件已经被统一到Planet组件中，使用sizeCategory参数区分大小

// 检测设备类型的函数
const isMobileDevice = () => {
  return (window.innerWidth <= 768) || 
         ('ontouchstart' in window) || 
         (navigator.maxTouchPoints > 0) || 
         (navigator.msMaxTouchPoints > 0);
};

function App() {
  const [isAiChatVisible, setIsAiChatVisible] = useState(false);
  const [isAiChatLoading, setIsAiChatLoading] = useState(false); // 添加加载状态
  const aiChatLoadedRef = useRef(false);
  const aiChatFrameRef = useRef(null);


  const toggleAIChat = (e) => {
    e.preventDefault();
    setIsAiChatVisible(prevVisible => {
      const newVisibility = !prevVisible;
      if (newVisibility && !aiChatLoadedRef.current && aiChatFrameRef.current) {
        setIsAiChatLoading(true); // 显示加载中状态
        aiChatFrameRef.current.src = 'https://ai.juven.wang/floating';
        aiChatLoadedRef.current = true;
      }
      return newVisibility;
    });
  };

  const handleAiChatLoad = () => {
    setIsAiChatLoading(false); // 隐藏加载中状态
  };
  
  // 添加 markdown 数据状态到 App 组件内部
  const [markdownData, setMarkdownData] = useState({
    personalInfo: null,
    experiences: [],
    projects: [],
    education: []
  });

  const [isLoadingContent, setIsLoadingContent] = useState(true);
  
  // 添加加载所有内容的 useEffect
  useEffect(() => {
    async function loadContent() {
      setIsLoadingContent(true);
      try {
        const content = await loadAllContent();
        setMarkdownData(content);
        console.log('从 Markdown 文件加载的所有内容:', content);
      } catch (error) {
        console.error('加载内容失败:', error);
      } finally {
        setIsLoadingContent(false);
        setIsLoadingProjects(false); // 同时更新项目加载状态
      }
    }
    
    loadContent();
  }, []);

  // 合并 markdown 数据与原始数据
  const mergedData = {
    personalInfo: markdownData.personalInfo || resumeData.personalInfo,
    experiences: markdownData.experiences.length > 0 ? markdownData.experiences : resumeData.experiences,
    projects: markdownData.projects.length > 0 ? markdownData.projects : resumeData.projects,
    education: markdownData.education.length > 0 ? markdownData.education : resumeData.education
  };
  
  // 根据设备类型设置不同的默认视角距离
  const getInitialCameraPosition = () => {
    if (isMobileDevice()) {
      return [0, 50, 160]; // 移动端视角距离更远，以显示更多内容
    } else {
      return [0, 50, 120]; // 桌面端视角距离
    }
  };
  
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedEducation, setSelectedEducation] = useState(null);
  const [cameraPosition, setCameraPosition] = useState(getInitialCameraPosition()); // 根据设备类型设置默认视角
  const [activeSkillFilter, setActiveSkillFilter] = useState(null);
  const [viewMode, setViewMode] = useState('3d'); // 新增：视图模式状态 ('3d' 或 '2d')
  const [isPlaying, setIsPlaying] = useState(false); // BGM 播放状态 - 初始设为 false
 // 是否处于导航激活状态
  const audioRef = useRef(null); // 引用 audio 元素
  // 添加黑屏渐入效果
  const [fadeIn, setFadeIn] = useState(false);
  // 保留项目加载状态，但不再需要单独加载项目
  const [, setIsLoadingProjects] = useState(true); // isLoadingProjects 暂时未使用，但保留以备将来使用
  
  // 页面加载后渐入效果
  useEffect(() => {
    // 页面加载后短暂延迟再渐入
    setTimeout(() => {
      setFadeIn(true);
    }, 100);
    // 注意：loadContent已经在其他useEffect中调用，这里无需重复调用
  }, []);
  
  // 添加URL路径解析和内容导航功能
  useEffect(() => {
    // 解析URL路径并跳转到相应内容
    // 解析URL路径并跳转到相应内容
const parseUrlAndNavigate = () => {
  // 确保数据已经加载完成
  if (mergedData.projects.length > 0 && !isLoadingContent) {
    // 获取当前URL路径
    const pathname = window.location.pathname;
    console.log("✅ 当前URL路径:", pathname);
    
    // 如果路径不是根路径，尝试匹配项目、工作经验或教育经历
    if (pathname !== '/') {
      // 从路径中提取名称，去掉开头的斜杠
      const slug = decodeURIComponent(pathname.substring(1));
      console.log("✅ 解码后的路径名:", slug);
      
      // 先尝试匹配项目，只通过ID匹配
      const matchedProject = mergedData.projects.find(project => project.id === slug);
      
      // 然后尝试匹配工作经验，只通过ID匹配
      const matchedExperience = !matchedProject ? mergedData.experiences.find(exp => exp.id === slug) : null;
      
      // 最后尝试匹配教育经历，只通过ID匹配
      const matchedEducation = (!matchedProject && !matchedExperience) ? 
        (mergedData.education?.find(edu => edu.id === slug) || null) : null;
      
      console.log("✅ 项目匹配结果:", matchedProject ? "找到匹配项目" : "未找到匹配项目");
      console.log("✅ 经验匹配结果:", matchedExperience ? "找到匹配经验" : "未找到匹配经验");
      console.log("✅ 教育匹配结果:", matchedEducation ? "找到匹配教育经历" : "未找到匹配教育经历");
      
      // 根据匹配结果打开相应详情面板
      if (matchedProject) {
        console.log("✅ 通过URL打开项目详情:", matchedProject.title || matchedProject.name || matchedProject.id);
        handleSatelliteClick(matchedProject);
      } else if (matchedExperience) {
        console.log("✅ 通过URL打开经验详情:", matchedExperience.title || matchedExperience.name || matchedExperience.id);
        handlePlanetClick(matchedExperience);
      } else if (matchedEducation) {
        console.log("✅ 通过URL打开教育经历详情:", matchedEducation.title || matchedEducation.name || matchedEducation.id);
        handleEducationClick(matchedEducation);
      }
    }
  }
};
    // const parseUrlAndNavigate = () => {
    //   // 确保数据已经加载完成
    //   if (mergedData.projects.length > 0 && !isLoadingContent) {
    //     // 获取当前URL路径
    //     const pathname = window.location.pathname;
    //     console.log("✅ 当前URL路径:", pathname);
        
    //     // 如果路径不是根路径，尝试匹配项目、工作经验或教育经历
    //     if (pathname !== '/') {
    //       // 从路径中提取名称，去掉开头的斜杠
    //       const slug = decodeURIComponent(pathname.substring(1));
    //       console.log("✅ 解码后的路径名:", slug);
          
    //       console.log("✅ 尝试匹配项目...");
    //       console.log("✅ 所有可用项目标题:", mergedData.projects.map(p => p.title || p.name));
    //       console.log("✅ 所有可用项目 ID:", mergedData.projects.map(p => p.id));
          
    //       console.log("✅ 尝试匹配经验...");
    //       console.log("✅ 所有可用经验 ID:", mergedData.experiences.map(e => e.id));
          
    //       console.log("✅ 尝试匹配教育经历...");
    //       console.log("✅ 所有可用教育经历 ID:", mergedData.education?.map(e => e.id) || []);

          
    //       // 先尝试匹配项目
    //       const matchedProject = mergedData.projects.find(project => 
    //         project.title === slug || 
    //         project.name === slug || 
    //         project.id === slug
    //       );
          
    //       // 然后尝试匹配工作经验
    //       const matchedExperience = !matchedProject ? mergedData.experiences.find(exp => 
    //         exp.id === slug || exp.title === slug || exp.name === slug
    //       ) : null;
          
    //       // 最后尝试匹配教育经历
    //       const matchedEducation = (!matchedProject && !matchedExperience) ? 
    //         (mergedData.education?.find(edu => 
    //           edu.id === slug || edu.title === slug || edu.name === slug
    //         ) || null) : null;
          
    //       console.log("✅ 项目匹配结果:", matchedProject ? "找到匹配项目" : "未找到匹配项目");
    //       console.log("✅ 经验匹配结果:", matchedExperience ? "找到匹配经验" : "未找到匹配经验");
    //       console.log("✅ 教育匹配结果:", matchedEducation ? "找到匹配教育经历" : "未找到匹配教育经历");
          
    //       // 根据匹配结果打开相应详情面板
    //       if (matchedProject) {
    //         console.log("✅ 通过URL打开项目详情:", matchedProject.title || matchedProject.name || matchedProject.id);
    //         handleSatelliteClick(matchedProject);
    //       } else if (matchedExperience) {
    //         console.log("✅ 通过URL打开经验详情:", matchedExperience.title || matchedExperience.name || matchedExperience.id);
    //         handlePlanetClick(matchedExperience);
    //       } else if (matchedEducation) {
    //         console.log("✅ 通过URL打开教育经历详情:", matchedEducation.title || matchedEducation.name || matchedEducation.id);
    //         handleEducationClick(matchedEducation);
    //       } else {
    //         // 如果没有匹配任何内容，尝试匹配相关经历
    //         const relatedExperience = mergedData.experiences.find(exp => exp.id === slug);
    //         if (relatedExperience) {
    //           // 计算行星位置
    //           const experienceIndex = mergedData.experiences.findIndex(
    //             exp => exp.id === relatedExperience.id
    //           );
              
    //           if (experienceIndex !== -1) {
    //             // 计算行星的3D位置
    //             const angle = (experienceIndex / mergedData.experiences.length) * Math.PI * 2;
    //             const distance = 30 + experienceIndex * 15;
    //             const x = Math.cos(angle) * distance;
    //             const z = Math.sin(angle) * distance;
                
    //             // 导航到行星位置
    //             navigateToPlanet([x, 0, z]);
                
    //             // 延迟一点时间后打开项目详情，确保相机已经移动到位
    //             setTimeout(() => {
    //               handleSatelliteClick(matchedProject);
    //             }, 800);
    //           } else {
    //             // 如果找不到行星位置，直接打开项目详情
    //             handleSatelliteClick(matchedProject);
    //           }
    //         } else {
    //           // 如果找不到关联经历，直接打开项目详情
    //           handleSatelliteClick(matchedProject);
    //         }
    //       }
    //     }
    //   }
    // };
    
    // 组件挂载后执行URL解析
    parseUrlAndNavigate();
    
    // 监听popstate事件，处理浏览器前进后退
    const handlePopState = () => {
      parseUrlAndNavigate();
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [mergedData.projects, mergedData.experiences, mergedData.education, isLoadingContent]);
  const [autoRotate, setAutoRotate] = useState(false); // 控制自动旋转
  const controlsRef = useRef(null); // 引用 OrbitControls
  const lastInteractionRef = useRef(Date.now()); // 记录最后交互时间
  const autoRotateTimeoutRef = useRef(null); // 存储自动旋转的计时器ID
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 跟踪用户交互并重置自动旋转计时器
  const handleUserInteraction = useCallback(() => {
    lastInteractionRef.current = Date.now();
    
    // 如果当前在自动旋转，停止它
    if (autoRotate) {
      setAutoRotate(false);
    }
    
    // 清除已有的计时器
    if (autoRotateTimeoutRef.current) {
      clearTimeout(autoRotateTimeoutRef.current);
    }
    
    // 设置新的计时器，3秒无操作后启动自动旋转
    autoRotateTimeoutRef.current = setTimeout(() => {
      setAutoRotate(true);
    }, 2000);
  }, [autoRotate]);

  // 用户交互事件监听器
  useEffect(() => {
    // 添加用户交互监听
    window.addEventListener('mousemove', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);
    window.addEventListener('click', handleUserInteraction);
    window.addEventListener('wheel', handleUserInteraction);
    
    // 初始化自动旋转计时器
    autoRotateTimeoutRef.current = setTimeout(() => {
      setAutoRotate(true);
    }, 2000);
    
    // 组件卸载时清理
    return () => {
      window.removeEventListener('mousemove', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('wheel', handleUserInteraction);
      
      if (autoRotateTimeoutRef.current) {
        clearTimeout(autoRotateTimeoutRef.current);
      }
    };
  }, [handleUserInteraction]);
  
  // 只在组件首次挂载时自动播放背景音乐
  useEffect(() => {
    // 自动播放背景音乐
    playBackgroundMusic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听窗口大小变化，自动调整相机位置
  useEffect(() => {
    // 定义窗口大小变化处理函数
    const handleResize = () => {
      // 只有在没有选中行星或项目时才自动调整相机位置
      if (!selectedExperience && !selectedProject) {
        setCameraPosition(getInitialCameraPosition());
      }
    };
    
    // 添加窗口大小变化监听器
    window.addEventListener('resize', handleResize);
    
    // 清除监听器
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [selectedExperience, selectedProject]);

  // 生成闪烁星星的位置
  const numTwinklingStars = 100;
  const twinklingStarPositions = useMemo(() => {
    const positions = [];
    for (let i = 0; i < numTwinklingStars; i++) {
      const r = 80; // Radius within which stars appear
      const phi = Math.random() * Math.PI; // Polar angle
      const theta = Math.random() * Math.PI * 2; // Azimuthal angle
      positions.push([
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.sin(phi) * Math.sin(theta),
        r * Math.cos(phi)
      ]);
    }
    return positions;
  }, []);
  
  // 轨道半径配置 - 动态计算轨道数量和半径
  const allOrbitRadii = useMemo(() => {
    // 合并所有天体数据并计算总数
    const totalBodies = [...mergedData.experiences, ...mergedData.projects].length;
    
    // 行星参数设置
    const basePlanetSize = 8; // 基础行星直径 - 增大行星估计尺寸
    const safetyFactor = 4; // 安全系数，确保行星间足够间距
    const minSpaceBetweenPlanets = basePlanetSize * safetyFactor; // 行星间最小间距
    
    // 轨道参数设置
    const baseOrbit = 20; // 第一条轨道半径
    const orbitIncrement = 15; // 轨道半径递增量，按用户要求设置为15
    const maxPlanetsInInnerOrbit = 3; // 内轨道最多行星数量
    
    // 计算每条轨道的最大容量（基于轨道周长和行星间距）
    const calculateMaxPlanetsForOrbit = (radius) => {
      const circumference = 2 * Math.PI * radius; // 轨道周长
      // 根据周长和最小间距计算最大容量
      return Math.max(maxPlanetsInInnerOrbit, Math.floor(circumference / minSpaceBetweenPlanets));
    };
    
    // 动态确定所需的轨道数量
    const orbits = [];
    let remainingPlanets = totalBodies;
    let currentRadius = baseOrbit;
    
    // 计算最少需要多少条轨道
    while (remainingPlanets > 0 && orbits.length < 7) { // 按用户要求设置最多7条轨道限制
      const maxPlanets = calculateMaxPlanetsForOrbit(currentRadius);
      orbits.push(currentRadius);
      remainingPlanets -= maxPlanets;
      currentRadius += orbitIncrement;
    }
    
    // 确保至少3条轨道，按用户要求
    while (orbits.length < 3) {
      orbits.push(currentRadius);
      currentRadius += orbitIncrement;
    }
    
    return {
      all: orbits,
      // 每条轨道的最大起容量，用于后续分配行星
      maxCapacities: orbits.map(radius => calculateMaxPlanetsForOrbit(radius))
    };
  }, [mergedData.experiences, mergedData.projects]);
  
  // 为所有轨道生成稳定的随机初始角度
  const orbitInitialAngles = useMemo(() => {
    // 为每条轨道生成一个随机初始角度
    return allOrbitRadii.all.map(() => Math.random() * Math.PI * 2);
  }, [allOrbitRadii.all]);
  
  // 统一的天体位置计算（经历和项目合并）
  const unifiedCelestialBodies = useMemo(() => {
    const result = [];
    
    // 合并所有天体数据（经历和项目）
    const allBodies = [
      ...mergedData.experiences.map(exp => ({ 
        type: 'experience', 
        data: exp, 
        importance: exp.importance || 2 
      })),
      ...mergedData.projects.map(proj => ({ 
        type: 'project', 
        data: proj, 
        importance: proj.importance || 1 
      }))
    ];
    
    // 按重要性排序（从高到低）
    const sortedBodies = [...allBodies].sort(
      (a, b) => b.importance - a.importance
    );
    
    const totalBodies = sortedBodies.length;
    
    // 如果没有天体，返回空结果
    if (totalBodies === 0) {
      return result;
    }
    
    const orbitsCount = allOrbitRadii.all.length;
    const maxCapacities = allOrbitRadii.maxCapacities || [];
    
    // 基于每条轨道的最大容量分配天体
    // 首先将重要的天体分配到内轨道，然后再向外分配
    
    // 先计算每条轨道的最大容量上限
    const bodiesPerOrbit = [];
    let remainingBodies = totalBodies;
    
    // 首先尝试按照最大容量分配
    for (let i = 0; i < orbitsCount; i++) {
      // 使用轨道最大容量作为上限，或者剩余天体数量，取较小值
      const maxForThisOrbit = maxCapacities[i] || 4; // 默认4个如果没有计算值
      const allocated = Math.min(remainingBodies, maxForThisOrbit);
      bodiesPerOrbit.push(allocated);
      remainingBodies -= allocated;
      
      // 如果已经分配完所有天体，则退出
      if (remainingBodies <= 0) break;
    }
    
    // 如果还有剩余天体，则创建额外轨道
    if (remainingBodies > 0) {
      console.warn(`警告: 轨道数量不足以容纳所有天体，还有${remainingBodies}个没有分配空间`);
    }
    
    let bodyIndex = 0;
    
    // 分配天体到轨道
    for (let i = 0; i < allOrbitRadii.all.length; i++) {
      const count = bodiesPerOrbit[i];
      if (count <= 0) continue;
      
      // 获取当前轨道的天体
      const orbitBodies = sortedBodies.slice(bodyIndex, bodyIndex + count);
      bodyIndex += count;
      
      const orbitRadius = allOrbitRadii.all[i];
      const orbitAngleOffset = orbitInitialAngles[i]; // 使用轨道的随机初始角度
      
      // 计算每个天体之间的角度间距
      const safeAngleStep = (Math.PI * 2) / Math.max(count, 1);
      
      // 加入随机扰动，确保天体不会完全均匀分布而导致可能的重叠
      const jitter = safeAngleStep * 0.2; // 添加20%的角度扰动
      
      // 计算每个天体的角度位置，加入微小随机扰动
      orbitBodies.forEach((body, j) => {
        const randomJitter = (Math.random() * jitter * 2) - jitter; // -jitter 到 +jitter 之间的随机值
        const angle = j * safeAngleStep + orbitAngleOffset + randomJitter;
        
        result.push({
          type: body.type,
          body: body.data,
          orbitRadius,
          angle,
          orbitIndex: i
        });
      });
    }
    
    return result;
  }, [mergedData.experiences, mergedData.projects, allOrbitRadii.all, orbitInitialAngles, allOrbitRadii.maxCapacities]);

  // mergedProjects已经在mergedData中定义，直接使用
  
  // 在界面上显示加载状态
  useEffect(() => {
    if (isLoadingContent) {
      console.log('正在加载内容...');
      // 这里可以添加加载指示器或其他UI反馈
    } else {
      console.log('内容加载完成');
    }
  }, [isLoadingContent]);
  
  // 已移除不需要的orbits变量 - 使用统一的天体系统替代
  
  console.log("Rendering planets:", mergedData.experiences);

  // 使用 useMemo 提取所有唯一技能 - 使用合并后的数据
  const allSkills = useMemo(() => {
    const skillsSet = new Set();
    mergedData.experiences.forEach(exp => {
      exp.skills && exp.skills.forEach(skill => skillsSet.add(skill));
    });
    mergedData.projects.forEach(proj => {
      proj.technologies && proj.technologies.forEach(tech => skillsSet.add(tech));
    });
    return Array.from(skillsSet).sort();
  }, [mergedData.experiences, mergedData.projects]);

  // 按时间排序的经历和项目，用于时间轴视图 - 使用合并后的数据
  const timelineItems = useMemo(() => {
    const items = [
      // 添加经历
      ...mergedData.experiences.map(exp => ({
        ...exp,
        type: 'experience',
        date: new Date(exp.startDate)
      })),
      // 添加项目（如果有日期数据）
      ...mergedData.projects.map(proj => {
        // 查找关联的经历来确定项目时间
        const relatedExp = mergedData.experiences.find(exp => exp.id === proj.relatedExperience);
        return {
          ...proj,
          type: 'project',
          date: relatedExp ? new Date(relatedExp.startDate) : new Date(0)
        };
      })
    ];
    
    // 按时间降序排序（最近的在前面）
    return items.sort((a, b) => b.date - a.date);
  }, [mergedData.experiences, mergedData.projects]);

  // BGM 控制
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(error => console.error("Error playing audio:", error));
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  const toggleBGM = () => {
    setIsPlaying(!isPlaying);
  };

  // 处理行星点击事件
  const handlePlanetClick = (experience) => {
    console.log("Planet clicked:", experience);
    // 导航状态相关的特效代码已被移除
    
    setSelectedExperience(experience);
    setSelectedProject(null);
    setSelectedEducation(null);
    
    // 更新URL而不刷新页面
    const experienceSlug = encodeURIComponent(experience.id);
    const newUrl = `/${experienceSlug}`;
    console.log("✅ 更新URL到:", newUrl);
    window.history.pushState({}, '', newUrl);
  };
  
  // 处理教育经历点击事件
  const handleEducationClick = (education) => {
    console.log("Education clicked:", education);
    
    setSelectedEducation(education);
    setSelectedExperience(null);
    setSelectedProject(null);
    
    // 更新URL而不刷新页面
    const educationSlug = encodeURIComponent(education.id);
    const newUrl = `/${educationSlug}`;
    console.log("✅ 更新URL到:", newUrl);
    window.history.pushState({}, '', newUrl);
  };

  // 处理项目卫星点击事件
  const handleSatelliteClick = (project) => {
    console.log("Satellite clicked:", project);
    setSelectedProject(project);
    setSelectedExperience(null);
    
    // 更新URL而不刷新页面
    const projectSlug = encodeURIComponent(project.title);
    const newUrl = `/${projectSlug}`;
    console.log("✅ 更新URL到:", newUrl);
    window.history.pushState({}, '', newUrl);
  };

  // 处理关闭详情面板事件
  const handleCloseDetails = () => {
    setSelectedExperience(null);
    setSelectedProject(null);
    setSelectedEducation(null); // 增加关闭教育经历面板
    // 可能还需要重置相机到默认视角，如果需要的话
    // resetCameraToDefault(); 
    
    // 恢复URL到根路径
    window.history.pushState({}, '', '/');
    console.log("✅ 关闭面板，重置URL到根路径");
  };

  // 前往行星位置
  const navigateToPlanet = (position) => {
    setCameraPosition([position[0], position[1] + 5, position[2] + 15]);
  };

  const handleSkillFilterClick = (skill) => {
    setActiveSkillFilter(prevSkill => prevSkill === skill ? null : skill);
  };

  const clearSkillFilter = () => {
    setActiveSkillFilter(null);
  };

  // 处理访问主站
  const handleVisitMainSite = () => {
    window.open('https://me.juven.wang', '_blank');
  };

  // 切换视图模式
  const toggleViewMode = () => {
    setViewMode(prev => prev === '3d' ? '2d' : '3d');
  };

  // 时间轴项目点击处理
  const handleTimelineItemClick = (item) => {
    if (item.type === 'experience') {
      // 查找对应行星的位置信息
      const index = resumeData.experiences.findIndex(exp => exp.id === item.id);
      if (index !== -1) {
        const angle = (index / resumeData.experiences.length) * Math.PI * 2;
        const distance = 30 + index * 15;
        const x = Math.cos(angle) * distance;
        const z = Math.sin(angle) * distance;
        
        // 切换到3D视图
        setViewMode('3d');
        
        // 导航到该行星并选中
        navigateToPlanet([x, 0, z]);
        handlePlanetClick(item);
      }
    } else if (item.type === 'project') {
      // 处理项目点击
      setSelectedProject(item);
      setSelectedExperience(null);
    }
  };

  // 播放背景音乐的函数
  const playBackgroundMusic = () => {
    // 开始播放音频并设置状态为播放中
    if (audioRef.current) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true); // 播放成功后更新状态
          console.log("音频播放开始");
        })
        .catch(error => {
          console.error("音频播放失败:", error);
          setIsPlaying(false);
        });
    }
  };

  // 添加移动端菜单切换函数
  const toggleMobileMenu = () => {
    setMobileMenuOpen(prev => !prev);
  };

  return (
    <div className="app-container">
      {/* 全屏加载动画 */}
      {isLoadingContent && (
        <div className="loading-overlay">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-text">宇宙正在生成中...</div>
          </div>
        </div>
      )}
      {/* 黑屏渐入效果 */}
      <div className={`fade-overlay ${fadeIn ? 'fade-out' : ''}`}></div>

      {/* 视图切换按钮 */}
      <button 
        className="view-toggle-btn"
        onClick={toggleViewMode}
        title={viewMode === '3d' ? '切换到时间轴视图' : '切换到3D宇宙视图'}
      >
        {viewMode === '3d' ? (
          <>
            <svg className="timeline-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13,4 L13,20 M6,4 L6,20 M20,4 L20,20 M3,8 L9,8 M10,16 L16,16 M17,12 L23,12" strokeWidth="2" stroke="currentColor" fill="none" />
            </svg>
            <span>时间轴视图</span>
          </>
        ) : (
          <>
            <svg className="universe-icon" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="5" />
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1" />
              <circle cx="17" cy="7" r="2" />
            </svg>
            <span>宇宙视图</span>
          </>
        )}
      </button>

      {/* BGM音乐播放器 */}
      <div className="music-player">
        <div className="song-info">
          <div className="song-text">
            <span>Dusty Springfield-Spooky</span>
          </div>
        </div>
        <button
          className="music-control-btn"
          onClick={toggleBGM}
          title={isPlaying ? '暂停背景音乐' : '播放背景音乐'}
        >
          <div className={`control-icon ${isPlaying ? 'pause' : 'play'}`}></div>
        </button>
      </div>
      <audio ref={audioRef} src="/bgm.flac" loop />

      {/* 3D 宇宙视图 */}
      {viewMode === '3d' && (
        <div className="universe-canvas-container">
          <Canvas 
            camera={{ position: cameraPosition, fov: 60 }} 
            dpr={[1, 2]} 
            onCreated={handleUserInteraction}
            shadows // 启用阴影
          >
                    {/* 添加星空背景 */}
          <UniverseBackground />
          
          {/* <fog attach="fog" args={['#0a0a2a', 300, 1200]} /> 降低雾效强度，增大开始和结束距离 */}
          
          {/* 增强光照系统 */}
          <ambientLight intensity={0.4} /> {/* 降低环境光强度，增加对比度 */}
          <directionalLight 
            position={[10, 15, 10]} 
            intensity={2.0} 
            castShadow 
            shadow-mapSize={[2048, 2048]} 
            shadow-bias={-0.0001}
          />
          <hemisphereLight skyColor="#b0c0ff" groundColor="#101020" intensity={0.8} />
          
          {/* 太阳点光源 */}
          {/* 主光源 - 增强中心炫光效果 */}
          <pointLight position={[0, 0, 0]} intensity={5} distance={50} decay={2} color="#fff7d0" />

          <Suspense fallback={<Html center><div className="loading"></div></Html>}>
            {/* 添加相机动画控制组件，传递必要的状态 */}
            <CameraAnimation 
              selectedExperience={selectedExperience} 
              selectedProject={selectedProject} 
            />
            
            {/* 宇宙背景 - 增加星星数量和深度 */}
            <Stars radius={300} depth={100} count={18000} factor={7} saturation={0.7} fade speed={0.6} />
            
            {/* 添加自定义闪烁星星 */}
            {twinklingStarPositions.map((pos, i) => (
              <TwinklingStar key={`twinkle-${i}`} position={pos} />
            ))}
            
            {/* 增强后期处理效果 */}
            <EffectComposer>
              {/* 增强的Bloom效果使光源发出耀眼辨光 */}
              <Bloom 
                intensity={2.0} 
                kernelSize={5} 
                luminanceThreshold={0.45} 
                luminanceSmoothing={0.2} 
                blendFunction={BlendFunction.SCREEN}
              />
              
              {/* 色彩偏移，增加科幻感 */}
              <ChromaticAberration
                offset={[0.002, 0.002]} 
                radialModulation={true}
                modulationOffset={0.15}
              />
              
              {/* 普通晚霄效果使边缘变暗 */}
              <Vignette 
                offset={0.5} 
                darkness={0.45} 
                eskil={false}
              />
              
              {/* 增强深度感 */}
              <SSAO 
                samples={9}
                radius={0.04}
                intensity={20}
                luminanceInfluence={0.6}
                scale={0.5}
              />
              
              {/* 已移除故障效果 */}
              
              {/* 增加轻微的噪点纹理效果 */}
              <Noise 
                premultiply 
                opacity={0.03} 
                blendFunction={BlendFunction.ADD}
              />
              
              {/* 只在面板展开时使用轻微景深效果 */}
              {(selectedExperience || selectedProject) && (
                <DepthOfField
                  focusDistance={0.03}
                  focalLength={0.012}
                  bokehScale={1.2}
                  height={700}
                />
              )}
            </EffectComposer>
            
            {/* 太阳/中心 - 代表个人 */}
            <Sun 
              position={[0, 0, 0]} 
              personalInfo={mergedData.personalInfo} 
              detailsOpen={!!selectedExperience || !!selectedProject || !!selectedEducation} 
            />
            
            {/* 行星和轨道 - 使用新的轨道系统 */}
            {/* 先绘制所有轨道线 - 关1-5条 */}
            {allOrbitRadii.all.map((radius, i) => (
              <OrbitLineComponent
                key={`main-orbit-${i}`}
                radius={radius}
                color="rgba(255, 255, 255, 0.08)" // 增加透明度
                lineWidth={i < 2 ? 0.8 : 0.6} // 增加线条粗细
                dashed={false} // 保持实线
                dashSize={0}
                gapSize={0}
              />
            ))}
            
            {/* 使用统一的天体系统渲染所有行星(经历和项目) */}
            {unifiedCelestialBodies.map((celestialBody, index) => {
              const { type, body, orbitRadius, angle, orbitIndex } = celestialBody;
              const isExperience = type === 'experience';
              
              // 计算天体位置
              const x = Math.cos(angle) * orbitRadius;
              const z = Math.sin(angle) * orbitRadius;
              const position = [x, 0, z];
              
              // 根据天体类型和轨道索引决定大小类别
              let sizeCategory = 2; // 默认中等大小
              
              if (isExperience) {
                // 经历行星通常更大
                sizeCategory = body.importance === 3 ? 3 : 2;
              } else {
                // 项目小行星通常更小
                sizeCategory = 1;
              }
              
              // 计算旋转速度 - 不同轨道有不同速度
              const orbitSpeed = 0.03 - orbitIndex * 0.01; // 降低公转速度
              
              return (
                <Planet
                  key={`${type}-${body.id || index}`}
                  // 传递轨道角度和半径，而不是绝对位置
                  orbitRadius={orbitRadius}
                  orbitAngle={angle} 
                  orbitSpeed={orbitSpeed} // 恢复行星公转速度
                  rotationSpeed={isExperience ? 0.5 : 1.5}
                  size={isExperience ? 4.5 : 2.5}
                  sizeCategory={sizeCategory}
                  texture={body.image 
                    ? body.image 
                    : body.planetTexture || 
                      (isExperience ? null : `/assets/textures/${['blue_planet', 'green_planet', 'purple_planet'][index % 3]}.jpg`)}
                  color={body.color || (isExperience ? '#FF5500' : '#888888')}
                  label={isExperience ? body.company : (body.title || body.name || '未命名项目')}
                  experience={body} // 统一使用experience参数传递数据
                  onClick={() => {
                    if (isExperience) {
                      handlePlanetClick(body);
                    } else {
                      handleSatelliteClick(body);
                    }
                    navigateToPlanet(position);
                  }}
                  activeSkillFilter={activeSkillFilter}
                  selectedExperience={selectedExperience}
                  selectedProject={selectedProject}
                />
              );
            })}
            
            {/* 轨道控制 */}
            <OrbitControls
              ref={(controls) => {
                controlsRef.current = controls;
                // 将控制器实例保存到全局，以便相机动画组件可以访问
                window.orbitControlsInstance = controls;
              }}
              enableZoom={true}
              enablePan={true}
              enableRotate={true}
              zoomSpeed={0.6}
              panSpeed={0.5}
              rotateSpeed={0.4}
              target={[0, 0, 0]}
              autoRotate={autoRotate}
              autoRotateSpeed={0.5}
              onChange={handleUserInteraction}
            />
          </Suspense>


        </Canvas>
        </div>
      )}

      {/* 2D 时间轴视图 */}
      {viewMode === '2d' && (
        <div className="timeline-container">
          <Timeline 
            items={timelineItems} 
            onItemClick={handleTimelineItemClick}
            selectedExperience={selectedExperience}
            selectedProject={selectedProject}
          />
        </div>
      )}
      
      {/* 移动端菜单切换按钮 */}
      {viewMode === '3d' && (
        <div className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          {mobileMenuOpen ? (
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </div>
      )}
      
      {/* UI 叠加层 */}
      <div className="ui-overlay">
        {/* 左侧面板 - 快速导航和技能筛选 */}
        <div className={`left-panels ${mobileMenuOpen ? 'active' : ''}`}>
          {viewMode === '3d' && (
            <>
              <nav className={`planet-navigation ${mobileMenuOpen ? 'active' : ''}`}>
                <h3>快速导航</h3>
                
                {/* 工作经验导航部分 */}
                <h4 className="navigation-section-title">工作经验</h4>
                <ul>
                  {mergedData.experiences.map((exp, index) => (
                    <li key={exp.id}>
                      <button
                        onClick={() => {
                          const angle = (index / mergedData.experiences.length) * Math.PI * 2;
                          const distance = 30 + index * 15;
                          const x = Math.cos(angle) * distance;
                          const z = Math.sin(angle) * distance;
                          navigateToPlanet([x, 0, z]);
                          handlePlanetClick(exp);
                          setMobileMenuOpen(false); // 点击导航项后关闭移动菜单
                        }}
                      >
                        <div className="planet-nav-dot" style={{ backgroundColor: exp.color || '#3498db' }}></div>
                        {exp.company || exp.title}
                      </button>
                    </li>
                  ))}
                </ul>
                
                {/* 教育经历导航部分 */}
                <h4 className="navigation-section-title">教育经历</h4>
                <ul>
                  {mergedData.education?.map((edu, index) => (
                    <li key={edu.id}>
                      <button
                        onClick={() => {
                          handleEducationClick(edu);
                          setMobileMenuOpen(false); // 点击导航项后关闭移动菜单
                        }}
                      >
                        <div className="planet-nav-dot" style={{ backgroundColor: edu.color || '#9b59b6' }}></div>
                        {edu.institution || edu.name || edu.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* 技能筛选面板 */}
              <div className={`skills-filter-panel ${mobileMenuOpen ? 'active' : ''}`}>
                <h4>技能筛选:</h4>
                <div className="skills-list">
                  {allSkills.map(skill => (
                    <button 
                      key={skill} 
                      onClick={() => {
                        handleSkillFilterClick(skill);
                        setMobileMenuOpen(false); // 点击技能过滤器后关闭移动菜单
                      }}
                      className={`skill-button ${activeSkillFilter === skill ? 'active' : ''}`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
                {activeSkillFilter && (
                  <button onClick={() => {
                    clearSkillFilter();
                    setMobileMenuOpen(false); // 清除过滤器后关闭移动菜单
                  }} className="clear-filter-btn">
                    显示全部
                  </button>
                )}
              </div>
              
              {/* 移除左侧面板中的主站按钮 */}
            </>
          )}
        </div>
        
        {/* 右侧面板保留空白以便布局平衡 */}
        <div className="right-panels">
        </div>
      </div>
      
      {/* 经历详情 - 使用合并后的数据 */}
      {selectedExperience && (
        <ExperienceDetails
          experience={selectedExperience}
          onClose={handleCloseDetails}
          projects={mergedData.projects.filter(
            p => p.relatedExperience === selectedExperience.id
          )}
        />
      )}
      
      {/* 使用 ProjectDetails 组件替换临时 div */}
      {selectedProject && (
        <ProjectDetails 
          project={selectedProject} 
          onClose={handleCloseDetails} 
        />
      )}

      {/* 并在render部分添加EducationDetails组件（在显示项目详情和经验详情的代码之后） */}
      {selectedEducation && (
        <EducationDetails 
          education={selectedEducation} 
          onClose={handleCloseDetails}
        />
      )}

      {/* AI Floating Button and Chat Container START */}
      <a href="javascript:void(0)" onClick={toggleAIChat} className="ai-floating-button" title="访问AI">
        <span>AI</span>
      </a>
      
      {/* 移动端和桌面端通用的主站按钮，统一放在右下角 */}
      <a href="javascript:void(0)" onClick={handleVisitMainSite} className="main-site-button" title="访问主站">
        <span>主站</span>
      </a>

      <div id="aiChatContainer" className={`ai-chat-container ${isAiChatVisible ? 'active' : ''}`}>
        {isAiChatLoading && (
          <div className="ai-chat-loading">
            <div className="loading-spinner"></div>
            <div className="loading-text">加载中...</div>
          </div>
        )}
        <iframe
          ref={aiChatFrameRef}
          id="aiChatFrame" 
          className="ai-chat-iframe"
          frameBorder="0"
          title="AI Chat Assistant"
          onLoad={handleAiChatLoad}
        ></iframe>
      </div>
      {/* AI Floating Button and Chat Container END */}
    </div>
  );
}

export default App;